import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 12) { toast.error('Password must be at least 12 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: password }, { withCredentials: true });
      setUser(res.data.user);
      toast.success('Password reset successfully!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#808080]">Invalid reset link. <a href="/login" className="text-[#00FF99] hover:underline">Back to login</a></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="w-full max-w-md p-8">
        <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
              <span className="text-[#00FF99]">LCBSE</span> CRM
            </h1>
            <p className="text-[#808080] text-sm mt-1">Set your new password</p>
          </div>
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">New Password</Label>
              <Input data-testid="reset-password-input" value={password} onChange={e => setPassword(e.target.value)} type="password" required
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Min 12 characters" />
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Confirm Password</Label>
              <Input data-testid="reset-confirm-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password" required
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Repeat password" />
            </div>
            <Button type="submit" data-testid="reset-submit" disabled={loading}
              className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md h-11">
              <Lock size={18} className="mr-2" />
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <a href="/login" className="block text-center text-sm text-[#808080] hover:text-white">Back to login</a>
          </form>
        </div>
      </div>
    </div>
  );
}
